import { NestedViewing, NestedHome, NestedUser } from "@habity/types";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { toZonedTime } from "date-fns-tz";
import { capitalizeFirstLetter } from "@habity/utils";
import { APP_TIMEZONE } from "@habity/constants";

// Note: Viewings are already filtered to future-only and sorted in the backend
export const getNextViewing = (viewings?: NestedViewing[]) => {
  if (!viewings || viewings.length === 0) return null;
  return viewings[0];
};

/**
 * Checks if a viewing is fully booked (only for single-slot viewings)
 * @param viewing - The viewing to check
 * @returns true if the viewing has a single slot and is fully booked
 */
export const isViewingFullyBooked = (viewing: NestedViewing): boolean => {
  const hasSingleSlot = viewing.viewing_slots?.length === 1;
  if (!hasSingleSlot || !viewing.viewing_slots) return false;
  
  const singleSlot = viewing.viewing_slots[0];
  if (!singleSlot || viewing.participants_per_slot === null) return false;
  
  const participantCount = singleSlot.viewing_participants?.length || 0;
  return participantCount >= viewing.participants_per_slot;
};

/**
 * Checks if a specific viewing slot is fully booked
 * @param slot - The viewing slot to check
 * @param maxParticipants - Maximum participants per slot from the viewing
 * @returns true if the slot is fully booked
 */
export const isViewingSlotFullyBooked = (
  slot: NonNullable<NestedViewing['viewing_slots']>[0],
  maxParticipants: number | null
): boolean => {
  if (maxParticipants === null) return false;
  const participantCount = slot.viewing_participants?.length || 0;
  return participantCount >= maxParticipants;
};

/**
 * Checks if a user has booked any viewing for a home
 * @param user - The user to check
 * @param home - The home with viewings
 * @returns true if the user has booked at least one viewing slot
 */
export const hasUserBookedViewing = (
  user: NestedUser | null | undefined,
  home: NestedHome
): boolean => {
  if (!user || !home.viewings) return false;

  return home.viewings.some((viewing) => {
    if (!viewing.viewing_slots || !Array.isArray(viewing.viewing_slots)) {
      return false;
    }
    return viewing.viewing_slots.some((slot) => {
      if (!slot.viewing_participants || !Array.isArray(slot.viewing_participants)) {
        return false;
      }
      return slot.viewing_participants.some(
        (participant) => participant && participant.user_id === user.id
      );
    });
  });
};

export const formatNextViewing = (
  viewing: NestedViewing,
  includeEndTime = false,
) => {
  // Convert UTC time to app timezone for consistent display across all devices
  const startInAppTimezone = toZonedTime(new Date(viewing.start_time), APP_TIMEZONE);
  
  let formatting = "EEEE d MMMM 'kl.' HH:mm";

  // Check if date has a time other than 00:00
  if (startInAppTimezone.getHours() === 0) {
    formatting = "EEEE	d MMMM";
  }

  const start = capitalizeFirstLetter(
    format(startInAppTimezone, formatting, {
      locale: sv,
    }),
  );

  let end = "";

  if (
    includeEndTime &&
    viewing.end_time
  ) {
    const endInAppTimezone = toZonedTime(new Date(viewing.end_time), APP_TIMEZONE);
    if (endInAppTimezone.getHours() !== 0) {
      end = format(endInAppTimezone, "-HH:mm", {
        locale: sv,
      });
    }
  }

  return start + end;
};
